import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { ReactComponent as AboutIcon } from "@icons/ha-smiley.svg";
import { ReactComponent as ChangePasswordIcon } from "@icons/change-password.svg";
import { ReactComponent as ContactIcon } from "@icons/mail.svg";
import { ReactComponent as InfoIcon } from "@icons/info.svg";
import { ReactComponent as PersonalDetailsIcon } from "@icons/account.svg";
import { ReactComponent as PreferencesIcon } from "@icons/settings.svg";
import { ReactComponent as StarRoundedIcon } from "@icons/star-rounded.svg";
import { EqualWebButton } from "UIPalette/EqualWebButton/EqualWebButton";
import { ReactComponent as WisdomSvg } from "shared/assets/WisdomLogo.svg";
import { Hideable } from "shared/UI/Hideable/Hideable";
import { SearchBar } from "./SearchBar";
import classNames from "classnames";
import { t } from "i18next";
import useComponentVisible from "shared/UI/Hooks/useComponentVisible";
import { useAuthVersioning } from "core/hooks/useAuthVersioning";
import { useMobilePlatform } from "core/hooks/useMobilePlatform";
import { ReactComponent as BackButtonIcon } from "@icons/back.svg";
import { Container, NavBarMenuOverlay } from "shared/UI/LayoutComponents/LayoutComponents";
import { useNavBarContext } from "core/state/NavBarContext/NavBarContext";
import NavBarNotifications from "UIPalette/NavBarNotifications/NavBarNotifications";
import React, { ComponentType, ReactNode, useEffect, useState } from "react";
import { fromKebabToTitleCase } from "shared/core/utils/string";
import { Avatar } from "shared/UI/Avatar/Avatar";
import { useUserContext } from "core/state/UserContext/UserContext";
import useIsMobile from "shared/core/hooks/useIsMobile";
import Button from "shared/UI/Buttons/Button";
import { trackNavClick } from "core/monitoring/Events";

export const NavMenuItem = ({
  path,
  text,
  Icon,
  onClick,
}: {
  path?: string;
  text: string;
  Icon: ComponentType<React.SVGProps<SVGSVGElement>>;
  onClick?: () => void;
}) => {
  const navigateHook = useNavigate();

  const handleNavigate = () => {
    onClick && onClick();
    if (path) {
      trackNavClick(path);
      navigateHook(path);
    }
  };

  return (
    <button
      className="w-full flex flex-row border-t border-border-grey align-middle justify-between px-4 py-1"
      aria-label={text}
      onClick={handleNavigate}
    >
      <div className="flex flex-row">
        <div className="flex justify-center items-center [&>*]:h-auto">
          <Icon className="w-6 h-full shrink-0 text-grey" />
        </div>
        <span className="px-4 py-2 text-md font-bold text-navy-blue">{text}</span>
      </div>
    </button>
  );
};

export const TopNavBar = ({ isNotMainRoute, backIcon }: { isNotMainRoute: boolean; backIcon?: ReactNode }) => {
  const location = useLocation();
  const { supportLink, insetPadding, isMobileApp } = useMobilePlatform();
  const { Logout } = useAuthVersioning();
  const navigate = useNavigate();
  const { backHandler: navigateBack, pageTitle: titleOverride } = useNavBarContext();
  const { targetAreaRef, isComponentVisible, setIsComponentVisible } = useComponentVisible<HTMLDivElement>(false);
  const [pageTitle, setPageTitle] = useState<string>();
  const { user } = useUserContext();
  const isMobileScreen = useIsMobile(1024);

  const isMobilePage = isMobileApp && pageTitle !== "Home";

  const headerStyles = {
    paddingTop: insetPadding ? `${insetPadding.top + 8}px` : undefined,
    height: insetPadding ? `${insetPadding.top + 80}px` : "80px",
  };

  useEffect(() => {
    if (!!titleOverride) {
      setPageTitle(titleOverride);
    } else {
      const pagePath = location?.pathname?.split("/").filter((string) => string !== "")[0];
      const parsedTitle = fromKebabToTitleCase(pagePath);
      setPageTitle(parsedTitle);
    }
  }, [location?.pathname, titleOverride]);

  const renderAvatar = () => <Avatar interactive={false} firstName={user?.forenames || ""} lastName={user?.surname || ""} />;

  const renderMobileAvatarBlock = () => (
    <button onClick={() => navigate("/account")} className="flex items-center gap-3 mr-auto">
      {renderAvatar()}
      <div className="flex flex-col">
        <h3 className="text-start !pb-0 max-w-[150px] text-ellipsis overflow-hidden whitespace-nowrap">{user?.forenames || ""}</h3>
        {!!process.env.REACT_APP_USE_WISDOM_REWARDS && (
          <div className="flex gap-1 items-center">
            <StarRoundedIcon />
            <span>75</span>
          </div>
        )}
      </div>
    </button>
  );

  const renderDesktopAvatarBlock = () => (
    <div data-testid="desktop-avatar-block" className="flex flex-col gap-4 items-center px-8 pt-8">
      <Avatar className="!h-24 !w-24 !text-4xl" firstName={user?.forenames || ""} lastName={user?.surname || ""} />
      <div className="flex gap-2">
        <h3 className="!pb-0 max-w-[150px] text-ellipsis overflow-hidden whitespace-nowrap">{user?.forenames || ""}</h3>
        {!!process.env.REACT_APP_USE_WISDOM_REWARDS && (
          <div className="flex gap-1 items-center">
            |
            <StarRoundedIcon />
            <span>75</span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <header style={headerStyles} className="flex items-center bg-white w-full fixed left-0 top-0 z-[9998] drop-shadow-md">
        <Container>
          <div className="flex items-center relative h-full">
            <Hideable className="flex flex-row justify-between h-full w-full items-center pt-2" hidden={!isMobilePage || !isMobileApp}>
              {isNotMainRoute && (
                <div>
                  <button aria-label={t("navigation.backButton")} onClick={() => navigateBack()}>
                    {backIcon ?? <BackButtonIcon className="text-brand-purple w-6" title={t("navigation.backButton")} />}
                  </button>
                </div>
              )}
              <h2 className="w-full text-center">{pageTitle}</h2>
              {isNotMainRoute && <div className="w-6" />}
            </Hideable>
            <Hideable hidden={isMobilePage}>
              {!isMobileApp && !isMobileScreen ? (
                <NavLink
                  className="flex items-center flex-grow w-4/6 h-full pr-3 lg:flex-grow-0 xs:pointer-events-none lg:pointer-events-auto sm:w-auto"
                  aria-label={t("navigation.homeNavigationLabel")}
                  to={"/home"}
                  onClick={() => trackNavClick("/home")}
                >
                  <WisdomSvg />
                </NavLink>
              ) : (
                renderMobileAvatarBlock()
              )}
              <div className="flex-row items-center flex-grow hidden h-full gap-6 ml-4 lg:inline-flex">
                <NavLink
                  to={"/wellbeing"}
                  aria-label={t("navigation.wellbeingNavigationLabel")}
                  className={({ isActive }) => classNames("top-nav-bar", { active: isActive })}
                  onClick={() => trackNavClick("/wellbeing")}
                >
                  {t("navigation.wellbeingButton")}
                </NavLink>
                <NavLink
                  to={supportLink}
                  aria-label={t("navigation.supportNavigationLabel")}
                  className={({ isActive }) => classNames("top-nav-bar", { active: isActive })}
                  onClick={() => trackNavClick(supportLink)}
                >
                  {t("navigation.supportButton")}
                </NavLink>
                <NavLink
                  to={"/resources"}
                  aria-label={t("navigation.resourcesNavigationLabel")}
                  className={({ isActive }) => classNames("top-nav-bar", { active: isActive })}
                  onClick={() => trackNavClick("/resources")}
                >
                  {t("navigation.resourcesButton")}
                </NavLink>
              </div>
              <EqualWebButton />
              <SearchBar />
              <NavBarNotifications />
              {!isMobileScreen && (
                <button
                  className="h-full mt-0 align-top inline-block"
                  aria-label={t("navigation.userAccountMenu.ariaUserMenu")}
                  onClick={() => setIsComponentVisible(!isComponentVisible)}
                >
                  {renderAvatar()}
                </button>
              )}
              <Hideable hidden={!isComponentVisible}>
                <div ref={targetAreaRef}>
                  <NavBarMenuOverlay className="right-0">
                    <div className="flex flex-col gap-4 w-82 max-h-[calc(100vh-100px)] overflow-y-auto">
                      {renderDesktopAvatarBlock()}
                      {!!process.env.REACT_APP_USE_WISDOM_REWARDS && (
                        <Button onClick={() => navigate("/profile")} className="mb-2" variant="text" text={t("navigation.seeProfile")} />
                      )}
                      <div>
                        {!process.env.REACT_APP_DISABLE_COMPONENTS_PAGE && (
                          <NavMenuItem path={"/components"} text={t("navigation.userAccountMenu.components")} Icon={InfoIcon} />
                        )}
                        <NavMenuItem
                          path={"/personal-details"}
                          text={t("navigation.userAccountMenu.personalDetails")}
                          Icon={PersonalDetailsIcon}
                        ></NavMenuItem>
                        <NavMenuItem path={"/preferences"} text={t("navigation.userAccountMenu.preferences")} Icon={PreferencesIcon}></NavMenuItem>
                        <NavMenuItem
                          path={"/account-security"}
                          text={t("navigation.userAccountMenu.accountSecurity")}
                          Icon={ChangePasswordIcon}
                        ></NavMenuItem>
                        <NavMenuItem
                          path={"/customer-solutions"}
                          text={t("navigation.userAccountMenu.contactSupport")}
                          Icon={ContactIcon}
                        ></NavMenuItem>
                        <NavMenuItem path={"/about-us"} text={t("navigation.userAccountMenu.about")} Icon={AboutIcon}></NavMenuItem>
                        <NavMenuItem path={"/faqs"} text={t("navigation.userAccountMenu.faqs")} Icon={InfoIcon}></NavMenuItem>
                        <NavMenuItem path={"/terms-and-conditions"} text={t("navigation.userAccountMenu.terms")} Icon={InfoIcon}></NavMenuItem>
                        <NavMenuItem path={"/privacy-policy"} text={t("navigation.userAccountMenu.privacy")} Icon={InfoIcon}></NavMenuItem>
                        <NavMenuItem
                          path={"/account-cookie-policy"}
                          text={t("navigation.userAccountMenu.cookiePolicy")}
                          Icon={InfoIcon}
                        ></NavMenuItem>
                        <Logout type="nav" />
                      </div>
                    </div>
                  </NavBarMenuOverlay>
                </div>
              </Hideable>
            </Hideable>
          </div>
        </Container>
      </header>
      <div ref={targetAreaRef} style={headerStyles}></div>
    </>
  );
};
